import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import {
  ChartBarIcon,
  HeartIcon,
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  UserGroupIcon,
  BookOpenIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

export default function OrganizationGreatCommissionStats({ orgId }) {
  const [dateFilter, setDateFilter] = useState('thisMonth');
  const [stats, setStats] = useState({
    activities: { total: 0, prayers: 0, connections: 0, newMembers: 0, disciplesAdded: 0 },
    storyMetrics: { spiritualDiscussions: 0, heardGospel: 0, experiencedLove: 0, believersEncouraged: 0, newFollowers: 0 },
    storiesCount: 0,
    totalDisciples: 0,
    totalMessages: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const start = dateFilter === 'allTime' ? null : startOfDay(startOfMonth(now));
        const end = dateFilter === 'allTime' ? null : endOfDay(endOfMonth(now));

        const newStats = {
          activities: { total: 0, prayers: 0, connections: 0, newMembers: 0, disciplesAdded: 0 },
          storyMetrics: { spiritualDiscussions: 0, heardGospel: 0, experiencedLove: 0, believersEncouraged: 0, newFollowers: 0 },
          storiesCount: 0,
          totalDisciples: 0,
          totalMessages: 0,
        };

        try {
        const activitiesRef = collection(db, 'organizations', orgId, 'activities');
        let activitiesSnap;
        try {
          if (start && end) {
            const q = query(
              activitiesRef,
              where('createdAt', '>=', Timestamp.fromDate(start)),
              where('createdAt', '<=', Timestamp.fromDate(end))
            );
            activitiesSnap = await getDocs(q);
          } else {
            activitiesSnap = await getDocs(query(activitiesRef, orderBy('createdAt', 'desc'), limit(2000)));
          }
        } catch (indexErr) {
          if (start && end && (indexErr?.code === 'failed-precondition' || indexErr?.message?.includes('index'))) {
            const allSnap = await getDocs(query(activitiesRef, orderBy('createdAt', 'desc'), limit(2000)));
            activitiesSnap = { docs: allSnap.docs.filter(d => {
              const data = d.data();
              const created = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : new Date(0));
              return created >= start && created <= end;
            }) };
          } else throw indexErr;
        }
        for (const d of activitiesSnap.docs) {
          const data = d.data();
          const t = data.type;
          newStats.activities.total++;
          if (t === 'prayer') newStats.activities.prayers++;
          else if (t === 'message' || t === 'in_person_meeting') newStats.activities.connections++;
          else if (t === 'member_joined' || t === 'joined') newStats.activities.newMembers++;
          else if (t === 'disciple_added') newStats.activities.disciplesAdded++;
        }
        } catch (e) {
          console.warn('Activities query failed:', e);
        }

        try {
        const storiesRef = collection(db, 'organizations', orgId, 'stories');
        const storiesSnap = await getDocs(query(storiesRef, limit(1000)));
        for (const storyDoc of storiesSnap.docs) {
          if (storyDoc.id === '_collection_init') continue;
          const data = storyDoc.data();
          const storyDate = data.createdAt?.toDate ? data.createdAt.toDate() : (data.sharedAt?.toDate ? data.sharedAt.toDate() : new Date(0));
          if (start && end && (storyDate < start || storyDate > end)) continue;
          newStats.storiesCount++;
          if (data.metrics) {
            newStats.storyMetrics.spiritualDiscussions += data.metrics.spiritualDiscussions || 0;
            newStats.storyMetrics.heardGospel += data.metrics.heardGospel || 0;
            newStats.storyMetrics.experiencedLove += data.metrics.experiencedLove || 0;
            newStats.storyMetrics.believersEncouraged += data.metrics.believersEncouraged || 0;
            newStats.storyMetrics.newFollowers += data.metrics.newFollowers || 0;
          }
        }
        } catch (e) {
          console.warn('Stories query failed:', e);
        }

        const memberIds = new Set();
        try {
        const membersSnap = await getDocs(collection(db, 'organizations', orgId, 'members'));
        membersSnap.docs.forEach(d => memberIds.add(d.id));
        const orgSnap = await getDoc(doc(db, 'organizations', orgId));
        const orgData = orgSnap.exists() ? orgSnap.data() : {};
        const creatorId = orgData.createdBy ? (typeof orgData.createdBy === 'string' ? orgData.createdBy : orgData.createdBy?.uid) : orgData.creatorId;
        if (creatorId) memberIds.add(creatorId);
        } catch (e) {
          console.warn('Members query failed:', e);
        }

        const allDiscipleIds = new Set();
        let totalMessagesCount = 0;
        for (const memberId of memberIds) {
        if (cancelled) return;
        try {
          const userSnap = await getDoc(doc(db, 'users', memberId));
          if (!userSnap.exists()) continue;
          const userData = userSnap.data();
          const discipleIds = userData.discipleIds || [];
          discipleIds.forEach(id => allDiscipleIds.add(id));
          const mentorAppID = userData.appID || memberId;
          for (const discipleId of discipleIds) {
            try {
              const messagesRef = collection(db, 'disciples', discipleId, 'mentorDetails', mentorAppID, 'messages');
              const smsRef = collection(db, 'disciples', discipleId, 'mentorDetails', mentorAppID, 'messagesSMS');
              const [msgSnap, smsSnap] = await Promise.all([getDocs(messagesRef), getDocs(smsRef)]);
              let count = msgSnap.size + smsSnap.size;
              if (start && end) {
                count = 0;
                [...msgSnap.docs, ...smsSnap.docs].forEach(docSnap => {
                  const d = docSnap.data();
                  const msgDate = d.sentAt?.toDate ? d.sentAt.toDate() : (d.createdAt?.toDate ? d.createdAt.toDate() : new Date(0));
                  if (msgDate >= start && msgDate <= end) count++;
                });
              }
              totalMessagesCount += count;
            } catch (_) {}
          }
        } catch (_) {}
        }
        newStats.totalDisciples = allDiscipleIds.size;
        newStats.totalMessages = totalMessagesCount;

        if (!cancelled) setStats(newStats);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [orgId, dateFilter]);

  const dateLabel = dateFilter === 'allTime' ? 'All time' : dateFilter === 'thisMonth' ? 'This month' : 'Custom';

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Great Commission Stats</h2>
      <p className="text-gray-500 text-sm mb-4">Impact metrics & activities</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {['thisMonth', 'allTime'].map((key) => (
          <button
            key={key}
            onClick={() => setDateFilter(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              dateFilter === key ? 'bg-[#3274B8] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {key === 'thisMonth' ? 'This month' : 'All time'}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mb-4">Showing: {dateLabel}</p>

      {loading ? (
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <StatCardSkeleton key={i} hasIcon />
              ))}
            </div>
          </div>
          <div className="mb-6">
            <div className="h-4 w-36 bg-gray-200 rounded mb-3" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <StatCardSkeleton key={i} />
              ))}
            </div>
          </div>
          <div>
            <div className="h-4 w-40 bg-gray-200 rounded mb-3" />
            <div className="grid grid-cols-2 gap-3">
              {[1, 2].map((i) => (
                <StatCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <ChartBarIcon className="w-4 h-4" /> Activities
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              <StatCard icon={ChartBarIcon} value={stats.activities.total} label="Total activities" color="blue" />
              <StatCard icon={HeartIcon} value={stats.activities.prayers} label="Prayers" color="purple" />
              <StatCard icon={ChatBubbleLeftRightIcon} value={stats.activities.connections} label="Connections" color="green" />
              <StatCard icon={UserPlusIcon} value={stats.activities.newMembers} label="New members" color="amber" />
              <StatCard icon={UserGroupIcon} value={stats.activities.disciplesAdded} label="Disciples added" color="indigo" />
            </div>
          </div>
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <BookOpenIcon className="w-4 h-4" /> Story metrics
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard value={stats.storiesCount} label="Stories shared" color="gray" />
              <StatCard value={stats.storyMetrics.spiritualDiscussions} label="Spiritual discussions" color="blue" />
              <StatCard value={stats.storyMetrics.heardGospel} label="Heard gospel" color="green" />
              <StatCard value={stats.storyMetrics.experiencedLove} label="Experienced love" color="purple" />
              <StatCard value={stats.storyMetrics.believersEncouraged} label="Believers encouraged" color="amber" />
              <StatCard value={stats.storyMetrics.newFollowers} label="New followers" color="indigo" />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <EnvelopeIcon className="w-4 h-4" /> Disciples & messages
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <StatCard value={stats.totalDisciples} label="Total disciples" color="green" />
              <StatCard value={stats.totalMessages} label="Total messages" color="blue" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCardSkeleton({ hasIcon }) {
  return (
    <div className="p-4 rounded-xl border border-gray-100 bg-white">
      {hasIcon && <div className="h-9 w-9 bg-gray-200 rounded-lg mb-2" />}
      <div className="h-8 w-12 bg-gray-200 rounded mb-2" />
      <div className="h-3 w-20 bg-gray-200 rounded" />
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color = 'gray' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700',
    purple: 'bg-purple-50 text-purple-700',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    gray: 'bg-gray-50 text-gray-700',
  };
  const c = colors[color] || colors.gray;
  return (
    <div className="p-4 rounded-xl border border-gray-100 bg-white">
      {Icon && (
        <div className={`inline-flex p-2 rounded-lg mb-2 ${c}`}>
          <Icon className="w-5 h-5" />
        </div>
      )}
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}
